import { Box } from "@chakra-ui/layout";
import { Environment, prisma, Publication } from "database";
import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";
import { BlogContent } from "ui/src/components/BlogContent/BlogContent";
import { Head } from "ui/src/components/Head";
import { Layout } from "ui/src/components/HomeDomain/Layout";
import { useSignUpToast } from "ui/src/components/SignUp/useSignUpToast";
import { BlogContentProvider } from "ui/src/hooks/BlogContentContext";
import { logEvent } from "utils/src/amplitude";
import { z } from "zod";

import type { FrontMatter } from "ui/src/types/frontmatter";

const ParamsSchema = z.object({
  site: z.string(),
  slug: z.string(),
});

const EnvironmentSchema = z.nativeEnum(Environment);
const PublicationSchema = z.nativeEnum(Publication);

interface IBlog {
  source: string;
  frontMatter: FrontMatter;
  headings: string[];
  slug: string;
  site: string;
}

export default function Blog({
  source,
  frontMatter,
  headings,
  slug,
  site,
}: IBlog) {
  // Set the newsletter toast to popup after 20000ms
  useSignUpToast();

  React.useEffect(() => {
    if (!frontMatter) return;

    void logEvent("Blog post viewed", {
      title: frontMatter.title,
      tags: frontMatter.tags,
    });
  }, [frontMatter]);

  return (
    <Layout site={site}>
      <Head
        site={site}
        title={
          frontMatter?.title
            ? `${frontMatter.title} | TheLastWeekIn.Dev`
            : "TheLastWeekIn.Dev"
        }
        description={frontMatter?.description}
        image={frontMatter?.image}
        urlSuffix={`/blog/${slug}`}
      />
      <Box pb={16}>
        <BlogContentProvider
          value={{
            source,
            headings,
            frontMatter,
            slug,
            site,
          }}
        >
          <BlogContent />
        </BlogContentProvider>
      </Box>
    </Layout>
  );
}

type Matter = {
  content: string;
  data: Record<string, unknown>;
};

const getPublication = (site: string) => {
  return PublicationSchema.parse(
    {
      rust: Publication.RUST,
      weekly: Publication.WEEKLY,
      golang: Publication.GOLANG,
      typescript: Publication.TYPESCRIPT,
      python: Publication.PYTHON,
    }[site]
  );
};

const getEnvironment = (env: string | undefined) => {
  if (!env) {
    throw new Error("NEXT_PUBLIC_VERCEL_ENV is not set");
  }

  return EnvironmentSchema.parse(
    {
      development: Environment.DEVELOPMENT,
      production: Environment.PRODUCTION,
      preview: Environment.PREVIEW,
    }[env]
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.NODE_ENV === "production") {
    const lastBuildTime = await prisma.staticSiteGeneration.findUnique({
      where: {
        environment: getEnvironment(process.env.NEXT_PUBLIC_VERCEL_ENV),
      },
      select: {
        updatedAt: true,
      },
    });

    const blogPosts = await prisma.blogPost.findMany({
      where: {
        updatedAt: {
          gte: lastBuildTime?.updatedAt ?? new Date(0),
        },
      },
    });

    // Update last attempted build time
    await prisma.staticSiteGeneration.upsert({
      where: {
        environment: getEnvironment(process.env.NEXT_PUBLIC_VERCEL_ENV),
      },
      update: {
        updatedAt: new Date(),
      },
      create: {
        updatedAt: new Date(),
        environment: getEnvironment(process.env.NEXT_PUBLIC_VERCEL_ENV),
      },
    });

    return {
      paths: blogPosts.map((blogPost) => ({
        params: {
          site: blogPost.publication.toString().toLowerCase(),
          slug: blogPost.edition.toString() ?? "1",
        },
      })),
      fallback: true,
    };
  }

  return {
    paths: [],
    fallback: true,
  };
};

// This function gets called at build time on server-side.
// It won't be called on client-side, so you can even do
// direct database queries. See the "Technical details" section.
export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  const { slug, site } = ParamsSchema.parse(params);
  const publication = PublicationSchema.parse(getPublication(site));
  const blogPost = await prisma.blogPost.findUnique({
    where: {
      publicationEdition: {
        edition: parseInt(slug),
        publication,
      },
    },
  });

  if (!blogPost || !blogPost.content) {
    throw new Error("Blog post not found");
  }

  const matter = await import("gray-matter").then((mod) => mod.default);
  const { content, data } = matter(blogPost.content) as Matter;

  // ! Adds in Ads throughout blog posts
  const levelTwoHeadings = content.match(/^(##.+)$/gm);
  const injectedAdsBeforeSections = levelTwoHeadings ? levelTwoHeadings : [];

  let adSenseContent = content;
  for (const heading of injectedAdsBeforeSections) {
    adSenseContent = adSenseContent.replace(
      heading,
      `\n\n<Adsense />\n\n${heading}`
    );
  }

  const serialize = await import("next-mdx-remote/serialize").then(
    (mod) => mod.serialize
  );
  const mdxSource = await serialize(adSenseContent, {
    scope: data,
  });

  const marked = await import("marked").then((mod) => mod.marked);
  const tokens = marked.lexer(content);
  const headings = tokens
    .filter((token) => token.type === "heading" && token.depth === 2)
    // @ts-expect-error: text does exist
    .map((token) => token.text as string);

  // Override the data property with the parsed data until it's clean up
  data.image = `/opengraph/${site}/newsletter-${slug}.png`;

  return {
    props: {
      source: mdxSource,
      frontMatter: data,
      headings,
      slug,
      site,
    },
    revalidate: false,
  };
};
